// The Score cell of an audit row — the movement of the score, in one line.
//
// The arrow used to read «what arrived → what it became». On a board whose
// operator is `best` or `set` those two are the same number, so the cell drew
// "49 → 49" over a score that had just moved from 15, and the real «before»
// was hidden inside the expanded row. The arrow is now the movement itself —
// BEFORE → AFTER — and what arrived comes back as a muted tail, only where it
// differs from the result.
//
// All three numbers are already on the row: `current_score` (before),
// `applied_score` (after) and `submitted_score` (what arrived). Nothing here
// asks the backend for anything new.
//
// Both audit tables — the Audit tab and the Overview card — render this, for
// the same reason `isRejected` lives beside them: two copies of one grammar
// drift apart.

import type { AuditEntry } from "./types";
import {
  isAdminAction,
  isPreTransactionalGuard,
  isRejected,
} from "./tabs/auditReason";
import styles from "./ScoreFlow.module.css";

const NUMBER_FMT = new Intl.NumberFormat("en-US");

/** Why the cell has no «before» to show, spelled out on hover. */
const FIRST_SUBMIT_HINT = "No score stored yet — the first submit of this cycle";
const UNREAD_HINT =
  "The guard fired before the score row was read — the score before is unknown";
/**
 * Anything else with no `current_score`: a row written before migration 0058
 * added the column. Only the two pre-transactional guards can name a reason,
 * so nothing else claims one — an `admin_deleted` from last week is not a
 * guard that fired early.
 */
const UNRECORDED_HINT = "The score before this event was not recorded";

/** Which of the three the row has earned. */
function beforeHint(row: AuditEntry, rejected: boolean): string {
  if (!rejected) return FIRST_SUBMIT_HINT;
  return isPreTransactionalGuard(row) ? UNREAD_HINT : UNRECORDED_HINT;
}

export function ScoreFlow({ row }: { row: AuditEntry }) {
  const rejected = isRejected(row);
  const before = row.current_score;
  const hasBefore = before !== undefined && before !== null;

  // The tail names what arrived, and only when the row turned it into
  // something else: `increment` transforms it, a refusal applies none of it.
  // Admin rows never show one — an admin sent nothing, and on `admin_adjusted`
  // `submitted_score` is a copy of the score before.
  const showSent =
    !isAdminAction(row) && row.submitted_score !== row.applied_score;

  // The mockup spaces the arrow with a literal blank on either side on top of
  // its 4px margins, and butts the tail straight against the ✗ — the middot
  // separating it is drawn by the stylesheet, not by the markup.
  return (
    <span className={`${styles.scoreFlow} ${rejected ? styles.scoreRejected : ""}`}>
      {hasBefore ? (
        <span>{NUMBER_FMT.format(before)}</span>
      ) : (
        // Nothing stored, or nothing read: an em dash says the entity had no
        // score, a question mark says this row never got to look.
        <span className={styles.unknown} title={beforeHint(row, rejected)}>
          {rejected ? "?" : "—"}
        </span>
      )}{" "}
      <span className={styles.arrow}>→</span>{" "}
      {rejected ? (
        <span className={styles.x} title={row.reject_reason}>
          ✗
        </span>
      ) : (
        // Not rejected is exactly "applied_score is there" — that is what
        // isRejected asks, and the cast is what says so to the compiler.
        <span>{NUMBER_FMT.format(row.applied_score as number)}</span>
      )}
      {showSent && (
        <span className={styles.sent}>
          sent {NUMBER_FMT.format(row.submitted_score)}
        </span>
      )}
    </span>
  );
}
